// Party-package "orderMeta" persistence (localStorage key `i101_order_meta`).
//
// Written by OrderPackage when a customer builds a party package. Read by Checkout and
// Payment as a LAST-RESORT fallback when React-Router state is lost — a reopened tab,
// back-from-Stripe, or a resumed session would otherwise drop the package breakdown.
//
// ⚠️ WHY THE GUARDS EXIST — order I101-D3FB5XAT (2026-08-03):
// A customer ordered ONE à-la-carte item (Chettinadu Chicken Fry, MediumTray, $70) but the
// kitchen ticket and confirmation email carried a 12-item "Package Tray Summary" plus a
// table of spice preferences from a completely different, earlier package. The money was
// right ($68.20) and `lines` was right — only the summary fields were wrong, and they were
// written straight into DynamoDB, so this was NOT a display cache that would age out. It
// would have confused the kitchen permanently.
//
// Two independent causes, both fixed here:
//   1. `i101_order_meta` was never cleared when an order completed (ThankYou cleared
//      i101_cart / i101_checkout / i101_checkout_draft but not this key), so it survived
//      indefinitely on that browser.
//   2. Payment read it UNCONDITIONALLY as a fallback. Checkout already guarded the same
//      read behind `isPackageFlow`; Payment did not, so the two drifted.
//
// The correctness guard is `cartHasPackage` — package meta may only ever attach to a cart
// that actually contains a package. The TTL is a secondary cleanup for abandoned sessions
// and is deliberately generous so it can never truncate a slow but legitimate checkout.

export const ORDER_META_KEY = 'i101_order_meta';

/** Abandoned-session cleanup only. Real checkouts finish in minutes; correctness comes
 *  from cartHasPackage, not from this. */
export const ORDER_META_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

/** A cart line is a package when its size reads as a package.
 *  Mirrors the check in Checkout.isPackageFlow and Payment.isPackageItem. */
export function isPackageItem(item) {
  const size =
    item?.size ?? item?.tray ?? item?.Tray ?? item?.TrayName ??
    item?.variant ?? item?.option ?? item?.sizeName ?? null;
  const s = String(size || '').trim().toLowerCase();
  return s === 'package' || s === 'packages' || s === 'party package';
}

export function cartHasPackage(cart) {
  return Array.isArray(cart) && cart.some(isPackageItem);
}

/** Persist the package breakdown, stamped so it can expire. */
export function writeOrderMeta(meta) {
  try {
    localStorage.setItem(
      ORDER_META_KEY,
      JSON.stringify({ ...(meta || {}), savedAt: Date.now() })
    );
  } catch {
    /* storage unavailable (private mode) — the router-state path still works */
  }
}

export function clearOrderMeta() {
  try { localStorage.removeItem(ORDER_META_KEY); } catch { /* ignore */ }
}

/**
 * Read the persisted package meta, refusing to return it when it could contaminate a
 * non-package order.
 *
 * @param {Array} cart  the CURRENT cart — meta is only returned if it holds a package
 * @returns {Object} the meta, or {} when absent/stale/not-a-package-order
 */
export function readOrderMeta(cart) {
  if (!cartHasPackage(cart)) {
    // Not a package order: any stored meta belongs to a different order. This is the
    // check whose absence produced I101-D3FB5XAT.
    return {};
  }
  let data;
  try {
    data = JSON.parse(localStorage.getItem(ORDER_META_KEY));
  } catch {
    return {};
  }
  if (!data || typeof data !== 'object') return {};

  // Undated entries predate this change; treat them as fresh (the package guard above
  // already makes them safe) so an in-flight checkout isn't broken by the deploy.
  const savedAt = Number(data.savedAt);
  if (Number.isFinite(savedAt) && Date.now() - savedAt > ORDER_META_TTL_MS) {
    clearOrderMeta();
    return {};
  }
  return data;
}
